import * as React from 'react';
import {
    BoxSkeleton,
    HorizontalDivider,
    ItemActions,
    LinkButton,
    SkeletonLoader,
    useL2DTheme,
} from '@europrocurement/flexy-components';
import {
    DossierRoutingContext,
    DossierSmallCard,
    useCurrentDossierCtx,
    useFormalitiesCtx,
    useLazyDataLoader,
} from '@europrocurement/l2d-modules';
import { ClientReference } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/ClientReference';
import { HeaderTitle } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/HeaderTitle';
import { PrescripteurOffreSection } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/PrescripteurOffreSection';
import { Tiers, useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { Box, SxProps, Tooltip, Typography } from '@mui/material';
import { useEditSousClient } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { useDispatch } from 'react-redux';
import { TiersAppDispatch, useTiersService } from '@europrocurement/l2d-domain';

type DetailDossierLeftNavBarProps = {
    prescripteur: Tiers | undefined;
    sx?: SxProps;
};
const DetailDossierLeftNavBar: React.FunctionComponent<DetailDossierLeftNavBarProps> = function ({
    prescripteur,
    sx,
}) {
    const { palette } = useL2DTheme();
    const { siblings, dossier } = useCurrentDossierCtx();
    const { getOrderRoute, getDossierRoute } = React.useContext(DossierRoutingContext);
    const { getTiersById } = useTiersCtx();
    const { getFormalityById } = useFormalitiesCtx();
    const { refreshTiers } = useLazyDataLoader();
    const { selectPrescripteur } = useTiersService();
    const dispatch = useDispatch<TiersAppDispatch>();

    const clientOpere = React.useMemo(
        () => (dossier?.clientOpereId ? getTiersById(dossier?.clientOpereId) : undefined),
        [dossier?.clientOpereId, getTiersById],
    );

    const sirenRequired = React.useMemo(
        () =>
            (dossier?.formalites || [])
                .map((formality) => formality.id)
                .map(getFormalityById)
                .find((formality) => formality?.sirenRequired) !== undefined,
        [dossier?.formalites, getFormalityById],
    );

    const formattedOffre = React.useMemo(
        () => ({
            libelle: dossier?.offreLibelle || 'Aucune offre',
        }),
        [dossier?.offreLibelle],
    );

    React.useEffect(() => {
        if (dossier?.prescripteurId) {
            dispatch(selectPrescripteur({ id: dossier?.prescripteurId as number }));
        }
    }, [dispatch, dossier?.prescripteurId, selectPrescripteur]);

    const [commandeHovered, setCommandeHovered] = React.useState(false);
    const [sirenHovered, setSirenHovered] = React.useState(false);

    const { editFolio } = useEditSousClient({
        sousClientId: clientOpere?.id,
        sirenRequired,
        onSuccess: () => refreshTiers(clientOpere?.id as number),
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'hidden',
                gap: '8px',
                // 100 vh screen - 5vh footer - 72px header page
                maxHeight: 'calc(95vh - 72px)',
                ...sx,
            }}
        >
            {/* Collection Card  */}
            <Box sx={{ flexShrink: 0 }}>
                <SkeletonLoader
                    isLoading={!dossier || !clientOpere}
                    skeleton={
                        <BoxSkeleton
                            sx={{ height: '300px', marginTop: '24px', borderRadius: '8px' }}
                        />
                    }
                >
                    <Box
                        data-testid="dossier_small_card"
                        sx={{
                            backgroundColor: palette.background.paper,
                            borderRadius: '8px',
                            padding: '14px',
                            border: `1px solid ${palette.grey[200]} `,
                            gap: '8px',
                            display: 'flex',
                            marginTop: '24px',
                            flexDirection: 'column',
                            boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                        }}
                    >
                        {dossier && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                                onMouseEnter={() => setCommandeHovered(true)}
                                onMouseLeave={() => setCommandeHovered(false)}
                            >
                                <LinkButton
                                    to={getOrderRoute(dossier.collectionId as number)}
                                    onClick={() => {}}
                                    wrapperSx={{
                                        backgroundColor: palette.grey[100],
                                        '&:hover': {
                                            backgroundColor: palette.grey[200],
                                            color: palette.grey[900],
                                        },
                                        color: palette.grey[700],
                                        padding: '6px 8px',
                                    }}
                                    textSx={{ fontSize: '0.8rem' }}
                                    label={`Commande ${dossier.collectionNumero as string}`}
                                />
                                <ItemActions
                                    copyValue={dossier.collectionNumero as string}
                                    visible={commandeHovered}
                                    slideDirection="left"
                                />
                            </Box>
                        )}
                        {clientOpere && (
                            <>
                                <Box>
                                    <HeaderTitle
                                        clientOpereRaisonSociale={clientOpere.raisonSociale}
                                        prescriberId={prescripteur?.id}
                                        sousClientId={clientOpere.id}
                                        onEdit={editFolio}
                                        demo={prescripteur?.demo}
                                    />
                                    <Typography variant="l2dbodyxs">
                                        {clientOpere?.adresse?.fullAddress as string}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}
                                    onMouseEnter={() => setSirenHovered(true)}
                                    onMouseLeave={() => setSirenHovered(false)}
                                >
                                    <Typography
                                        variant="l2dbodys"
                                        color={palette.text.primary}
                                    >
                                        SIREN:
                                    </Typography>
                                    {clientOpere.siren ? (
                                        <Tooltip title="Consulter sur societe.com">
                                            <span style={{ display: 'inline-flex' }}>
                                                <LinkButton
                                                    to={`https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere.siren}`}
                                                    target="_blank"
                                                    onClick={() => {}}
                                                    wrapperSx={{
                                                        backgroundColor: palette.grey[100],
                                                        '&:hover': {
                                                            backgroundColor: palette.grey[200],
                                                            color: palette.grey[900],
                                                        },
                                                        fontWeight: 500,
                                                        color: palette.grey[700],
                                                        padding: '6px 8px',
                                                    }}
                                                    label={clientOpere.siren}
                                                />
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Typography
                                            variant="l2dbodys"
                                            sx={{ fontWeight: 500, color: palette.grey[700] }}
                                        >
                                            À renseigner
                                        </Typography>
                                    )}
                                    {!(
                                        prescripteur?.id !== undefined &&
                                        prescripteur?.id === clientOpere.id
                                    ) && (
                                        <ItemActions
                                            onEdit={editFolio}
                                            editTooltip="Éditer le SIREN de la société"
                                            copyValue={clientOpere.siren}
                                            visible={sirenHovered}
                                            slideDirection="left"
                                        />
                                    )}
                                </Box>
                            </>
                        )}
                        {dossier && siblings?.length === 1 && (
                            <ClientReference
                                societeRef={dossier.societeRef as string}
                                dossierId={dossier.id as number}
                            />
                        )}

                        {/* Prescripteur et offre */}
                        {prescripteur && (
                            <PrescripteurOffreSection
                                prescripteur={{
                                    ...prescripteur,
                                    codeComptable:
                                        prescripteur.codeComptable ?? 'Pas de code comptable',
                                }}
                                offre={formattedOffre}
                                palette={palette}
                                source="BACK"
                            />
                        )}
                    </Box>
                </SkeletonLoader>
            </Box>

            {/* Dossiers Card  */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <SkeletonLoader
                    isLoading={!siblings || !dossier}
                    skeleton={<BoxSkeleton sx={{ height: '200px', borderRadius: '8px' }} />}
                >
                    <Box>
                        {dossier && siblings && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <HorizontalDivider />

                                <Typography
                                    variant="l2dbodym"
                                    sx={{ color: palette.grey[700], paddingLeft: '10px' }}
                                >
                                    Dossiers ({siblings.length})
                                </Typography>

                                {siblings
                                    .sort((a, b) => Number(a.deleted) - Number(b.deleted))
                                    .map((doss) => (
                                        <DossierSmallCard
                                            key={doss.id}
                                            dossier={doss}
                                            isCurrentDossier={doss.id === dossier.id}
                                            link={getDossierRoute(doss.id as number)}
                                            mulitpleDossier={siblings.length > 1}
                                        />
                                    ))}
                            </Box>
                        )}
                    </Box>
                </SkeletonLoader>
            </Box>
        </Box>
    );
};

export default DetailDossierLeftNavBar;
